import { FC, PropsWithChildren, useContext, useEffect, useMemo } from 'react';

import { JoinChannels, LeaveChannels } from '@/api/dtos';
import ServiceStackClientContext from '@/contexts/api/ServiceStackClientContext';
import SignalRContext, { ISignalRContext, ISignalRListeners, ISignalROnClose, ISignalROnConnect, ISignalROnJoin, ISignalROnLeave, ISignalROnMessage, ISignalROnReconnect, ISignalROnReconnecting, ISignalRUnsubscribe, } from '@/contexts/api/SignalRContext';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr/dist/esm';
import { JsonServiceClient } from '@servicestack/client';


interface ISignalRProviderProps {
    baseUrl: string;
    debug?: boolean;
}

class SignalRManager implements ISignalRContext {
    private listeners: Record<string, ISignalRListeners> = {};
    private connection?: HubConnection;
    private client?: JsonServiceClient;
    private baseUrl: string;
    private debug: boolean;
    private activeChannels: Set<string> = new Set<string>();

    constructor(baseUrl: string, debug: boolean, client?: JsonServiceClient) {
        this.baseUrl = baseUrl;
        this.debug = debug;
        this.client = client;
    }

    public setClient = (client?: JsonServiceClient) => {
        this.client = client;
    }

    private getListeners = <TListener = Function>(type: keyof ISignalRListeners, channel?: string): TListener[] => {
        if (!channel) {
            return Object.keys(this.listeners).flatMap(x => this.listeners[x][type] as unknown as TListener[]);
        }

        return this.listeners[channel]?.[type] as unknown as TListener[] ?? [];
    };

    private getChannels = () => {
        return Object.keys(this.listeners);
    };

    private addChannelEventHandler = (channel: string) => {
        this.connection?.on(channel, async (data) => {
            if (this.debug) {
                console.log(`SignalRManager - onMessage - ${channel}`, data);
            }

            for (let listener of this.getListeners<ISignalROnMessage>('onMessage', channel)) {
                await listener(this.connection!, data);
            }
        })
    }

    private removeChannelEventHandler = (channel: string) => {
        this.connection?.off(channel);
    }

    private joinChannels = async (channels?: string[]) => {

        if (!channels || channels.length === 0) {
            return;
        }

        if (!this.connection?.connectionId) {
            return;
        }

        await this.client?.post(new JoinChannels({
            channels: channels,
            connectionId: this.connection.connectionId
        }));

        for (let channel of channels) {
            this.addChannelEventHandler(channel);
            this.activeChannels.add(channel);
            await this.onChannelJoined(channel);
        }
    }

    private leaveChannels = async (channels?: string[]) => {
        if (!channels || channels.length === 0) {
            return;
        }

        if (!this.connection?.connectionId) {
            return;
        }

        await this.client?.post(new LeaveChannels({
            channels: channels,
            connectionId: this.connection.connectionId
        }));

        for (let channel of channels) {
            this.removeChannelEventHandler(channel);
            this.activeChannels.delete(channel);
            await this.onChannelLeave(channel);
        }
    }

    private onChannelJoined = async (channel?: string) => {
        if (this.debug) {
            console.log('SignalRManager - onJoin', channel);
        }

        for (let listener of this.getListeners<ISignalROnJoin>('onJoin', channel)) {
            await listener(this.connection!);
        }
    }

    private onChannelLeave = async (channel?: string) => {
        if (this.debug) {
            console.log('SignalRManager - onLeave', channel);
        }

        for (let listener of this.getListeners<ISignalROnLeave>('onLeave', channel)) {
            await listener(this.connection!);
        }
    }

    private onConnect = async () => {
        if (this.debug) {
            console.log('SignalRManager - onConnect');
        }

        for (let listener of this.getListeners<ISignalROnConnect>('onConnect', undefined)) {
            await listener(this.connection!);
        }
    }

    private onReconnected = async (connectionId?: string) => {
        if (this.debug) {
            console.log('SignalRManager - onReconnect', connectionId);
        }

        if (connectionId) {
            this.joinChannels(this.getChannels());
        }

        for (let listener of this.getListeners<ISignalROnReconnect>('onReconnect', undefined)) {
            await listener(this.connection!, connectionId);
        }
    }

    private onReconnecting = async (error?: Error) => {
        if (this.debug) {
            console.log('SignalRManager - onReconnecting', error);
        }

        for (let listener of this.getListeners<ISignalROnReconnecting>('onReconnecting', undefined)) {
            await listener(this.connection!, error);
        }
    }

    private onClose = async (error?: Error) => {
        if (this.debug) {
            console.log('SignalRManager - onClose', error);
        }

        for (let listener of this.getListeners<ISignalROnClose>('onClose', undefined)) {
            await listener(this.connection!, error);
        }
    }

    private createConnection = async () => {
        this.connection = new HubConnectionBuilder()
            .withUrl(`${this.baseUrl}signal-r/sdv`, { withCredentials: false })
            .withKeepAliveInterval(10000)
            .withAutomaticReconnect()
            .build();

        if (this.debug) {
            console.log('SignalRManager - Created Client', this.connection);
        }

        this.connection.onreconnected(this.onReconnected);
        this.connection.onreconnecting(this.onReconnecting);
        this.connection.onclose(this.onClose);

        await this.connection.start();
        await this.onConnect();
        await this.joinChannels(this.getChannels());
    }

    private disposeConnection = async () => {
        if (!this.connection) {
            return;
        }

        if (this.debug) {
            console.log('SignalRManager - Disposed of Connection', this.connection);
        }

        await this.connection.stop();
        this.connection = undefined;
        this.activeChannels = new Set<string>();
    }

    private subscriptionDelay = 0;

    private subscriptionTimer: number | undefined =  undefined;

    private updateSubscription = async () => {
        const channels = this.getChannels();

        if (!this.connection) {
            if (channels.length > 0) {

                if (this.debug) {
                    console.log('SignalRManager - Update Subscription', channels);
                }

                // subscriptions but no connection - create connection
                await this.createConnection();
                return;
            }

            // no subscripts and no connection - done;
            return;
        }

        if (channels.length === 0) {

            if (this.debug) {
                console.log('SignalRManager - Update Subscription', channels);
            }

            await this.disposeConnection();
            return;
        }

        const prevChannels = Array.from(this.activeChannels);

        const newChannels = channels.filter(x => !prevChannels.includes(x));
        const oldChannels = prevChannels.filter(x => !channels.includes(x));

        if (newChannels.length > 0 || oldChannels.length > 0) {

            if (this.debug) {
                console.log('SignalRManager - Update Subscription', channels);
            }
            
            await this.joinChannels(newChannels);
            await this.leaveChannels(oldChannels);
        }
    };

    private subscribe = (_channel: string) => {
        if (this.subscriptionTimer) {
            window.clearTimeout(this.subscriptionTimer);
        }

        this.subscriptionTimer = window.setTimeout(() => this.updateSubscription(), this.subscriptionDelay);
    }

    private unsubscribe = (_channel: string) => {
        if (this.subscriptionTimer) {
            window.clearTimeout(this.subscriptionTimer);
        }

        this.subscriptionTimer = window.setTimeout(() => this.updateSubscription(), this.subscriptionDelay);
    }

    private ensureChannel = (channel: string) => {
        if (this.listeners[channel] !== undefined) {
            return;
        }

        this.listeners[channel] = {
            onConnect: [],
            onJoin: [],
            onLeave: [],
            onMessage: [],
            onReconnect: [],
            onClose: [],
            onReconnecting: []
        }

        this.subscribe(channel);
    }

    private cleanChannel = (channel: string) => {
        if (this.listeners[channel] === undefined) {
            return;
        }

        this.listeners[channel].onConnect ??= [];
        this.listeners[channel].onJoin ??= [];
        this.listeners[channel].onLeave ??= [];
        this.listeners[channel].onMessage ??= [];
        this.listeners[channel].onReconnect ??= [];
        this.listeners[channel].onClose ??= [];
        this.listeners[channel].onReconnecting ??= [];

        if (
            this.listeners[channel].onConnect.length === 0 &&
            this.listeners[channel].onJoin.length === 0 &&
            this.listeners[channel].onLeave.length === 0 &&
            this.listeners[channel].onMessage.length === 0 &&
            this.listeners[channel].onReconnect.length === 0 &&
            this.listeners[channel].onClose.length === 0 &&
            this.listeners[channel].onReconnecting.length === 0
        )
        {
            delete this.listeners[channel];
            this.unsubscribe(channel);
        }
    }

    public addOnConnectListener = (
        channel: string, 
        listener: ISignalROnConnect
    ): ISignalRUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onConnect.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onConnect = this.listeners[channel].onConnect.filter(x => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnJoinListener = (
        channel: string, 
        listener: ISignalROnJoin
    ): ISignalRUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onJoin.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onJoin = this.listeners[channel].onJoin.filter(x => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnLeaveListener = (
        channel: string, 
        listener: ISignalROnLeave
    ): ISignalRUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onLeave.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onLeave = this.listeners[channel].onLeave.filter(x => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnReconnectingListener = (
        channel: string, 
        listener: ISignalROnReconnecting
    ): ISignalRUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onReconnecting.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onReconnecting = this.listeners[channel].onReconnecting.filter(x => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnMessageListener = <TData = any>(
        channel: string, 
        listener: ISignalROnMessage<TData>
    ): ISignalRUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onMessage.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onMessage = this.listeners[channel].onMessage.filter(x => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnReconnectListener = (
        channel: string, 
        listener: ISignalROnReconnect
    ): ISignalRUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onReconnect.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onReconnect = this.listeners[channel].onReconnect.filter(x => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnCloseListener = (
        channel: string, 
        listener: ISignalROnClose
    ): ISignalRUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onClose.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onClose = this.listeners[channel].onClose.filter(x => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public dispose = async () => {
        await this.disposeConnection();
    }
}

const SignalRProvider: FC<PropsWithChildren<ISignalRProviderProps>> = ({ 
    children,
    baseUrl,
    debug
}) => {
    const client = useContext(ServiceStackClientContext);

    const manager = useMemo(() => new SignalRManager(baseUrl, debug === true, client), []);

    useEffect(() => {
        manager.setClient(client);
    }, [client, manager])

    useEffect(() => {
        return () => {
            manager.dispose();
        }
    }, [manager])

    return (
        <SignalRContext.Provider value={manager}>
            {children}
        </SignalRContext.Provider>
    )
}

export default SignalRProvider